<template>
  <div v-if="tabs.length > 0" class="h-10 bg-white border-b border-gray-100 flex items-center px-4 gap-3 shrink-0">
    <div class="flex-1 min-w-0 overflow-x-auto overflow-y-hidden visited-tabs-scroll">
      <div class="flex items-center gap-2 min-w-max">
        <button
          v-for="tab in tabs"
          :key="tab.fullPath"
          type="button"
          class="group h-7 max-w-[180px] inline-flex items-center gap-1.5 rounded-md border px-2.5 text-xs transition-colors"
          :class="isActive(tab)
            ? 'bg-indigo-50 border-indigo-200 text-indigo-700'
            : 'bg-gray-50 border-gray-100 text-gray-500 hover:bg-gray-100 hover:text-gray-700'"
          @click="handleActivate(tab)"
        >
          <el-icon v-if="tab.pinned" class="text-[13px] shrink-0"><House /></el-icon>
          <span class="truncate">{{ tab.title }}</span>
          <el-icon
            v-if="!tab.pinned"
            class="text-[13px] shrink-0 rounded hover:bg-black/5"
            :title="`关闭${tab.title}`"
            :aria-label="`关闭${tab.title}`"
            @click.stop="handleClose(tab)"
          >
            <Close />
          </el-icon>
        </button>
      </div>
    </div>

    <el-dropdown trigger="click" @command="handleCommand">
      <button
        type="button"
        class="h-7 w-7 rounded-md border border-gray-100 bg-gray-50 text-gray-500 hover:bg-gray-100 hover:text-gray-700 flex items-center justify-center transition-colors"
        title="标签操作"
      >
        <el-icon><MoreFilled /></el-icon>
      </button>
      <template #dropdown>
        <el-dropdown-menu>
          <el-dropdown-item command="closeOthers" :disabled="tabs.length <= 1">关闭其他</el-dropdown-item>
          <el-dropdown-item command="closeAll" :disabled="closableTabsCount === 0">关闭全部</el-dropdown-item>
        </el-dropdown-menu>
      </template>
    </el-dropdown>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { ElMessageBox } from 'element-plus'
import { Close, House, MoreFilled } from '@element-plus/icons-vue'
import {
  isVisitedTabDirty,
  removeAllClosableVisitedTabs,
  removeOtherVisitedTabs,
  removeVisitedTab,
  visitedTabsState,
  type VisitedTab,
} from '../config/visitedTabs'

const route = useRoute()
const router = useRouter()

const tabs = computed(() => visitedTabsState.tabs)
const activeFullPath = computed(() => route.fullPath)
const closableTabsCount = computed(() => tabs.value.filter(tab => !tab.pinned).length)

const isActive = (tab: VisitedTab) => tab.fullPath === activeFullPath.value

const getFallbackTab = (closingTab: VisitedTab) => {
  const currentTabs = tabs.value
  const closingIndex = currentTabs.findIndex(tab => tab.fullPath === closingTab.fullPath)
  const remainingTabs = currentTabs.filter(tab => tab.fullPath !== closingTab.fullPath)
  if (remainingTabs.length === 0) return null

  return remainingTabs[Math.min(closingIndex, remainingTabs.length - 1)]
    || remainingTabs[closingIndex - 1]
    || remainingTabs[0]
}

const confirmCloseIfNeeded = async (tab: VisitedTab) => {
  if (!isActive(tab)) return true
  if (!tab.closeConfirm && !isVisitedTabDirty(tab.fullPath)) return true

  try {
    await ElMessageBox.confirm(
      '关闭后当前页面不会缓存，未保存的编辑、筛选或弹窗状态将丢失。确定关闭？',
      '关闭当前标签',
      {
        confirmButtonText: '关闭',
        cancelButtonText: '取消',
        type: 'warning',
      }
    )
    return true
  } catch (_) {
    return false
  }
}

const handleActivate = (tab: VisitedTab) => {
  if (isActive(tab)) return
  router.push(tab.fullPath)
}

const handleClose = async (tab: VisitedTab) => {
  if (tab.pinned) return

  const shouldClose = await confirmCloseIfNeeded(tab)
  if (!shouldClose) return

  const fallbackTab = isActive(tab) ? getFallbackTab(tab) : null
  removeVisitedTab(tab.fullPath)

  if (fallbackTab) {
    router.push(fallbackTab.fullPath)
  }
}

const handleCommand = async (command: string | number | object) => {
  if (command === 'closeOthers') {
    removeOtherVisitedTabs(activeFullPath.value)
    return
  }

  if (command === 'closeAll') {
    try {
      await ElMessageBox.confirm(
        '关闭全部标签后将回到工作台，当前页面不会缓存。',
        '关闭全部标签',
        {
          confirmButtonText: '关闭全部',
          cancelButtonText: '取消',
          type: 'warning',
        }
      )
    } catch (_) {
      return
    }

    removeAllClosableVisitedTabs()
    if (route.path !== '/dashboard') {
      router.push('/dashboard')
    }
  }
}
</script>

<style scoped>
.visited-tabs-scroll {
  scrollbar-width: thin;
}

.visited-tabs-scroll::-webkit-scrollbar {
  height: 4px;
}

.visited-tabs-scroll::-webkit-scrollbar-thumb {
  background: #d1d5db;
  border-radius: 999px;
}
</style>
